/**
 * Event Schema Component
 * Provides structured data for consultations, webinars, and events
 * Targets "solar consultation" and "solar webinar" queries
 */

import { useEffect } from "react";

interface EventSchemaProps {
  name?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  eventMode?: "Online" | "Offline" | "Mixed";
  location?: string;
  organizer?: string;
  eventType?: "BusinessEvent" | "EducationEvent" | "SaleEvent";
  offers?: {
    price: number;
    priceCurrency: string;
    availability: string;
  };
}

const EventSchema = ({
  name = "Free Solar Consultation",
  description = "15-minute personalized energy advisor consultation. Review your utility bills, assess solar potential, and learn how to achieve energy independence with $Zero down solar + battery storage.",
  startDate = new Date().toISOString(),
  endDate,
  eventMode = "Online",
  location = "https://www.gosolar.pro/#schedule-bottom",
  organizer = "California Energy Group",
  eventType = "BusinessEvent",
  offers = {
    price: 0,
    priceCurrency: "USD",
    availability: "https://schema.org/InStock"
  }
}: EventSchemaProps) => {

  useEffect(() => {
    // Generate recurring event dates (next 30 days of available consultations)
    const generateEventDates = () => {
      const events = [];
      const now = new Date();
      
      for (let i = 0; i < 30; i++) {
        const eventDate = new Date(now);
        eventDate.setDate(now.getDate() + i);
        
        // Skip weekends for consultations
        if (eventDate.getDay() === 0 || eventDate.getDay() === 6) continue;
        
        // Morning slot (9 AM PT)
        const morningSlot = new Date(eventDate);
        morningSlot.setHours(9, 0, 0, 0);
        
        // Afternoon slot (2 PM PT)
        const afternoonSlot = new Date(eventDate);
        afternoonSlot.setHours(14, 0, 0, 0);
        
        events.push(morningSlot.toISOString(), afternoonSlot.toISOString());
      }
      
      return events.slice(0, 10); // Return next 10 slots
    };

    const eventSchemas = generateEventDates().map((date, index) => ({
      "@context": "https://schema.org",
      "@type": eventType,
      "@id": `https://www.gosolar.pro/#event-${index}`,
      "name": name,
      "description": description,
      "startDate": date,
      "endDate": new Date(new Date(date).getTime() + 15 * 60000).toISOString(), // 15 min duration
      "eventStatus": "https://schema.org/EventScheduled",
      "eventAttendanceMode": eventMode === "Online" 
        ? "https://schema.org/OnlineEventAttendanceMode"
        : eventMode === "Offline"
          ? "https://schema.org/OfflineEventAttendanceMode"
          : "https://schema.org/MixedEventAttendanceMode",
      "location": eventMode === "Online" ? {
        "@type": "VirtualLocation",
        "url": location
      } : {
        "@type": "Place",
        "name": location
      },
      "organizer": {
        "@type": "Organization",
        "@id": "https://www.gosolar.pro/#organization",
        "name": organizer,
        "url": "https://www.gosolar.pro"
      },
      "performer": {
        "@type": "Organization",
        "name": "California Energy Group"
      },
      "offers": {
        "@type": "Offer",
        "price": offers.price.toString(),
        "priceCurrency": offers.priceCurrency,
        "availability": offers.availability,
        "url": "https://www.gosolar.pro/#schedule-bottom",
        "validFrom": new Date().toISOString()
      },
      "isAccessibleForFree": true,
      "inLanguage": ["en", "es"]
    }));

    const scriptId = "event-schema";
    let script = document.getElementById(scriptId) as HTMLScriptElement;
    
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = scriptId;
      document.head.appendChild(script);
    }
    
    script.textContent = JSON.stringify(eventSchemas);

    return () => {
      const toRemove = document.getElementById(scriptId);
      if (toRemove) toRemove.remove();
    };
  }, [name, description, startDate, endDate, eventMode, location, organizer, eventType, offers]);

  return null;
};

// Free consultation event (most important for conversions)
export const ConsultationEventSchema = () => (
  <EventSchema
    name="Free Solar + Battery Consultation"
    description="Schedule a free 15-minute call with a California Energy Advisor. Learn how to escape rising utility rates with $Zero down solar + battery storage. No obligation."
    eventMode="Online"
    eventType="BusinessEvent"
    offers={{ price: 0, priceCurrency: "USD", availability: "https://schema.org/InStock" }}
  />
);

// NEM 3.0 educational webinar
export const NEM3WebinarSchema = () => (
  <EventSchema
    name="NEM 3.0 Solar Savings Webinar"
    description="Learn how California's NEM 3.0 policy affects solar economics and why battery storage is now essential for maximum savings. Live Q&A included."
    eventMode="Online"
    eventType="EducationEvent"
    offers={{ price: 0, priceCurrency: "USD", availability: "https://schema.org/InStock" }}
  />
);

export default EventSchema;
